import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Avatar } from "@/components/conversas/Avatar";
import { Balao } from "@/components/conversas/Balao";
import { BotaoAssumir } from "@/components/conversas/BotaoAssumir";
import { Compositor } from "@/components/conversas/Compositor";
import { EnviarModelo } from "@/components/conversas/EnviarModelo";
import { FaixaPendencia } from "@/components/conversas/FaixaPendencia";
import { FichaLateral } from "@/components/conversas/FichaLateral";
import { RolarParaOFim } from "@/components/conversas/RolarParaOFim";
import { Icone } from "@/components/Icone";
import { comoChamar, formatarTelefone } from "@/lib/painel/telefone";
import type { DetalheConversa } from "@/lib/telas/conversas";
import { conversaQuery, conversasQuery, euQuery } from "../consultas";
import { useEscrevendo } from "../escrevendo";
import { marcarVista } from "../lidas";

const THREAD = "thread";
const CHAVE_FICHA = "lummichat_ficha";

/**
 * A conversa aberta: cabeçalho, a conversa e a caixa de escrever, com a ficha
 * do paciente ao lado (ou por cima, numa tela estreita).
 *
 * Os dados já estão no navegador quando esta tela desenha: o roteador os busca
 * ANTES de trocar de tela (e, com o mouse em cima do nome, antes do clique).
 */
export function Conversa({ telefone }: { telefone: string }) {
  const { data } = useSuspenseQuery(conversaQuery(telefone));
  const { data: lista } = useSuspenseQuery(conversasQuery());
  const { data: eu } = useSuspenseQuery(euQuery());
  const { det, nomes, quemAssumiu } = data;
  const nome = comoChamar(det.nome, det.telefone);
  const [verFicha, setVerFicha] = useFichaAberta();
  const escrevendo = useEscrevendo(det.telefone);

  // Aberta na tela = vista até a última mensagem — inclusive a que chegar com
  // ela aberta. É isso que apaga o "nova" da lista (lidas.ts).
  const ultimaISO = det.mensagens.at(-1)?.emISO;
  useEffect(() => {
    if (ultimaISO) marcarVista(eu.clinica.id, det.telefone, ultimaISO);
  }, [eu.clinica.id, det.telefone, ultimaISO]);

  const assistenteNoControle = !det.atendimentoHumano;
  const temModelos = det.capacidades.templates;
  const modelo = (variante: "cabecalho" | "icone" | "primario") => (
    <EnviarModelo
      telefone={det.telefone}
      modelos={det.modelos}
      pessoa={{ nome: det.nome, interesse: det.ficha?.interesse ?? null }}
      custoUSD={det.capacidades.custoPorTemplateUSD}
      variante={variante}
    />
  );

  return (
    <>
      <section className="bg-chat flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center gap-2.5 border-b border-[var(--color-borda)] bg-[var(--color-cartao)] px-2 md:gap-3 md:px-4">
          <Link
            to="/conversas"
            aria-label="Voltar para a lista"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--color-suave)] hover:bg-[var(--color-painel)] md:hidden"
          >
            <Icone nome="voltar" className="h-5 w-5" />
          </Link>
          <Avatar iniciais={det.iniciais} telefone={det.telefone} tamanho="md" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[15.5px] leading-tight font-bold text-[var(--color-tinta)]">{nome}</p>
            <p className="truncate text-[12.5px] text-[var(--color-suave)]">
              {det.nome ? `${formatarTelefone(det.telefone)} · ` : ""}
              {det.atendimentoHumano ? `atendida por ${quemAssumiu ?? "alguém da equipe"}` : "o assistente está respondendo"}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {assistenteNoControle ? (
              <span className="hidden h-6 items-center gap-1.5 rounded-full bg-[var(--color-realce)] px-2.5 text-xs font-semibold text-[var(--color-marca-forte)] lg:flex">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--color-marca)] opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--color-marca)]" />
                </span>
                Assistente respondendo
              </span>
            ) : (
              <span className="hidden h-6 items-center gap-1.5 rounded-full bg-[var(--color-atencao-fundo)] px-2.5 text-xs font-semibold text-[var(--color-atencao)] lg:flex">
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                Você está atendendo
              </span>
            )}
            {temModelos && det.janela.aberta && <span className="hidden md:contents">{modelo("cabecalho")}</span>}
            {det.janela.aberta && <BotaoAssumir telefone={det.telefone} atendimentoHumano={det.atendimentoHumano} curto />}
            <button
              type="button"
              onClick={() => setVerFicha(!verFicha)}
              aria-label={verFicha ? "Esconder a ficha" : "Mostrar a ficha"}
              aria-pressed={verFicha}
              className={
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors " +
                (verFicha ? "bg-[var(--color-realce)] text-[var(--color-marca-forte)]" : "text-[var(--color-suave)] hover:bg-[var(--color-painel)]")
              }
            >
              <Icone nome="painel-lateral" className="h-5 w-5" />
            </button>
          </div>
        </header>

        {det.pendencia && <FaixaPendencia telefone={det.telefone} pendencia={det.pendencia} />}

        <div id={THREAD} className="min-h-0 flex-1 overflow-y-auto py-4">
          <div className="mx-auto flex max-w-[880px] flex-col gap-1.5 px-3 md:px-5">
            {linhaDoTempo(det, nomes, eu.userId).map((item) =>
              item.tipo === "dia" ? (
                <div key={item.chave} className="my-2 flex justify-center">
                  <span className="rounded-full bg-[var(--color-cartao)] px-3 py-1 text-xs font-semibold text-[var(--color-suave)] shadow-cartao">
                    {item.texto}
                  </span>
                </div>
              ) : item.tipo === "aviso" ? (
                <div key={item.chave} className="my-1.5 flex justify-center">
                  <span className="flex items-center gap-1.5 rounded-full bg-[var(--color-painel)] px-3 py-1 text-xs text-[var(--color-suave)] shadow-[inset_0_0_0_1px_var(--color-borda)]">
                    <Icone nome="equipe" className="h-3.5 w-3.5" />
                    {item.texto} · {item.hora}
                  </span>
                </div>
              ) : item.tipo === "nota" ? (
                <div key={item.chave} className="my-1.5 flex justify-center">
                  <div className="max-w-[min(78%,560px)] rounded-xl bg-[var(--color-nota)] px-3.5 py-2.5 text-[13.5px] text-[var(--color-nota-tinta)]">
                    <p className="mb-0.5 flex items-center gap-1.5 text-xs font-bold">
                      <Icone nome="nota" className="h-3.5 w-3.5" />
                      Nota interna · {item.autor}
                    </p>
                    <p className="whitespace-pre-wrap">{item.texto}</p>
                    <p className="mt-0.5 text-[11px] opacity-65">{item.hora} · o paciente não vê</p>
                  </div>
                </div>
              ) : (
                <Balao key={item.chave} autor={item.autor} texto={item.texto} hora={item.hora} />
              ),
            )}
            {escrevendo && !det.atendimentoHumano && (
              <div className="flex justify-end" role="status" aria-label="O assistente está escrevendo">
                <div className="flex gap-1 rounded-[14px] rounded-tr-[4px] bg-[var(--color-agente)] px-3.5 py-3">
                  {[0, 1, 2].map((n) => (
                    <span key={n} className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--color-marca-forte)]" style={{ animationDelay: `${n * 180}ms` }} />
                  ))}
                </div>
              </div>
            )}
          </div>
          <RolarParaOFim alvo={THREAD} chave={`${det.mensagens.at(-1)?.id ?? ""}-${det.notas.length}-${escrevendo}`} />
        </div>

        <footer className="shrink-0 px-2.5 pt-2 pb-2.5 md:px-4 md:pb-3.5">
          <div className="mx-auto max-w-[880px]">
            {!det.janela.aberta ? (
              <Barra icone="cadeado" tom="atencao" texto={det.janela.texto}>
                {temModelos && modelo("primario")}
              </Barra>
            ) : assistenteNoControle ? (
              <Barra icone="agente" tom="marca" texto="O assistente está cuidando desta conversa. Para escrever, assuma antes — senão os dois falam ao mesmo tempo.">
                <BotaoAssumir telefone={det.telefone} atendimentoHumano={false} />
              </Barra>
            ) : (
              <Compositor key={det.telefone} telefone={det.telefone} antes={temModelos ? modelo("icone") : undefined} />
            )}
          </div>
        </footer>
      </section>

      {verFicha && (
        <>
          {/* Numa tela estreita a ficha abre por cima; o fundo escurecido fecha ao tocar. */}
          <button type="button" aria-label="Fechar a ficha" onClick={() => setVerFicha(false)} className="absolute inset-0 z-10 bg-black/30 xl:hidden" />
          <FichaLateral
            key={det.telefone}
            det={det}
            sugestoes={lista.etiquetasEmUso}
            nomes={nomes}
            userIdAtual={eu.userId}
            onFechar={() => setVerFicha(false)}
            className="absolute inset-y-0 right-0 z-20 w-full shadow-flutua sm:w-[340px] xl:static xl:z-auto xl:shrink-0 xl:border-l xl:shadow-none"
          />
        </>
      )}
    </>
  );
}

/** A barra de baixo quando não dá para escrever: o motivo, e o que fazer. */
function Barra({ icone, tom, texto, children }: { icone: "cadeado" | "agente"; tom: "atencao" | "marca"; texto: string; children?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-[18px] bg-[var(--color-cartao)] py-3 pr-3 pl-4 shadow-cartao">
      <span
        className={
          "flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full " +
          (tom === "atencao" ? "bg-[var(--color-atencao-fundo)] text-[var(--color-atencao)]" : "bg-[var(--color-realce)] text-[var(--color-marca-forte)]")
        }
      >
        <Icone nome={icone} className="h-4 w-4" />
      </span>
      <p className="min-w-[200px] flex-1 text-[13.5px] leading-relaxed text-[var(--color-suave)]">{texto}</p>
      {children}
    </div>
  );
}

/**
 * A ficha fica aberta ou fechada do jeito que a pessoa deixou — de uma
 * conversa para a outra e de um dia para o outro. Sem escolha, abre só onde
 * cabe ao lado (tela de 1280px ou mais).
 */
function useFichaAberta(): [boolean, (v: boolean) => void] {
  const [aberta, setAberta] = useState(() => {
    if (typeof window === "undefined") return true;
    let salvo: string | null = null;
    try {
      salvo = localStorage.getItem(CHAVE_FICHA);
    } catch {}
    return salvo !== "0" && window.innerWidth >= 1280;
  });
  return [
    aberta,
    (v) => {
      setAberta(v);
      // Só a tela larga guarda a escolha: no celular a ficha é um painel que se abre e fecha a cada olhada.
      if (window.innerWidth >= 1280) {
        try {
          localStorage.setItem(CHAVE_FICHA, v ? "1" : "0");
        } catch {}
      }
    },
  ];
}

type Item =
  | { tipo: "dia"; chave: string; texto: string }
  | { tipo: "msg"; chave: string; autor: string; texto: string; hora: string }
  | { tipo: "nota"; chave: string; autor: string; texto: string; hora: string }
  | { tipo: "aviso"; chave: string; texto: string; hora: string };

const horaDe = (iso: string) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

/** O mesmo rótulo de dia que o servidor dá às mensagens, para notas e avisos. */
function diaDe(iso: string): string {
  const d = new Date(iso);
  const inicio = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dias = Math.round((inicio(new Date()) - inicio(d)) / 86_400_000);
  if (dias === 0) return "Hoje";
  if (dias === 1) return "Ontem";
  if (dias < 7) return d.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit" });
  return d.toLocaleDateString("pt-BR");
}

/**
 * Mensagens, notas da equipe e o aviso à equipe numa linha só, pela hora em que
 * aconteceram, com o separador de dia entre um dia e outro. A nota aparece
 * onde foi escrita — "Ver com a Helena o desconto" ao lado do pedido de
 * desconto, e não só lá na ficha.
 */
function linhaDoTempo(det: DetalheConversa, nomes: Record<string, string>, userId: string): Item[] {
  const eventos: Array<{ emISO: string; dia: string; item: Item }> = [
    ...det.mensagens.map((m) => ({
      emISO: m.emISO,
      dia: m.dia,
      item: { tipo: "msg" as const, chave: m.id, autor: m.autor, texto: m.texto, hora: m.hora },
    })),
    ...det.notas.map((n) => ({
      emISO: n.emISO,
      dia: diaDe(n.emISO),
      item: {
        tipo: "nota" as const,
        chave: `nota-${n.id}`,
        autor: n.autorUserId === userId ? "você" : (nomes[n.autorUserId] ?? "equipe"),
        texto: n.texto,
        hora: horaDe(n.emISO),
      },
    })),
    ...(det.pendencia
      ? [
          {
            emISO: det.pendencia.abertaEmISO,
            dia: diaDe(det.pendencia.abertaEmISO),
            item: { tipo: "aviso" as const, chave: "pendencia", texto: "O assistente avisou a equipe", hora: horaDe(det.pendencia.abertaEmISO) },
          },
        ]
      : []),
  ];
  // `sort` é estável: duas coisas no mesmo instante ficam na ordem em que chegaram.
  eventos.sort((a, b) => Date.parse(a.emISO) - Date.parse(b.emISO));

  const itens: Item[] = [];
  let diaAtual = "";
  for (const e of eventos) {
    if (e.dia !== diaAtual) {
      diaAtual = e.dia;
      itens.push({ tipo: "dia", chave: `dia-${e.emISO}`, texto: e.dia.charAt(0).toUpperCase() + e.dia.slice(1) });
    }
    itens.push(e.item);
  }
  return itens;
}
